/**
 * Alan düzeyi erişim (WP 2.8, kapsam K-1…K-4): özel alanların kim tarafından okunup yazılabileceğinin TEK kaynağı.
 * Sunucu bu fonksiyonla metadata, tanım listesi, liste kataloğu, detay, yama ve aday ucu yanıtlarını projekte eder;
 * istemci yalnız sunucunun döndürdüğü projeksiyonu gösterir (arayüz güvenlik sınırı değildir).
 *
 * Principal eşleşmesi mevcut kuraldır (yeni yetki sistemi yok): kullanıcı; kullanıcının departmanı veya üst
 * departmanlarından biri (kayıt ACL'siyle aynı yön — departmana verilen hak alt departmanlara iner); şirket geneli.
 * Tenant sahibi ve `system_admin` kısıtsızdır. Liste boşsa alan herkese açıktır.
 */
import { z } from 'zod';
import { customFieldDefSchema, customFieldOptionsSchema, isValuelessFieldType, } from './custom-fields.js';
import { formulaFields, parseFormula } from './formula.js';
/** Etkin izinlerden alan görüntüleyicisi: tenant sahibi ve `system_admin` kısıtsız. */
export function fieldViewerOf(perms) {
    return {
        userId: perms.userId,
        deptAncestorIds: perms.deptAncestorIds,
        unrestricted: perms.isOwner || perms.role === 'system_admin',
    };
}
export function principalsMatch(principals, viewer) {
    if (viewer.unrestricted || principals.length === 0)
        return true;
    return principals.some((p) => p.type === 'company'
        ? true
        : p.type === 'user'
            ? p.id === viewer.userId
            : viewer.deptAncestorIds.includes(p.id));
}
/** Alanın başvurduğu diğer özel alan anahtarları (formül ifadesi + koşul alanı). */
export function fieldDependencies(def, customKeys) {
    const deps = new Set();
    if (def.type === 'formula' && def.options.expression) {
        try {
            for (const r of formulaFields(parseFormula(def.options.expression)))
                deps.add(r);
        }
        catch {
            // Ayrıştırılamayan formül: hesaplanamaz (değer null); bağımlılık yok sayılır
        }
    }
    if (def.condition !== null)
        deps.add(def.condition.field);
    return [...deps].filter((k) => customKeys.has(k));
}
/**
 * Okuma/yazma kümeleri. Kural geçişlidir: okunamayan alana (formülle veya koşulla) bağlı alan da okunamaz; böylece
 * hesaplanan değer ya da görünürlük sonucu gizli değer hakkında bilgi sızdırmaz (fail-closed).
 */
export function fieldAccessFor(defs, viewer) {
    const customKeys = new Set(defs.map((d) => d.key));
    const readable = new Set(defs.filter((d) => principalsMatch(d.readPrincipals, viewer)).map((d) => d.key));
    const deps = new Map(defs.map((d) => [d.key, fieldDependencies(d, customKeys)]));
    let changed = true;
    while (changed) {
        changed = false;
        for (const key of [...readable]) {
            if ((deps.get(key) ?? []).some((dep) => !readable.has(dep))) {
                readable.delete(key);
                changed = true;
            }
        }
    }
    const writable = new Set(defs
        .filter((d) => readable.has(d.key) &&
        d.type !== 'formula' &&
        !isValuelessFieldType(d.type) &&
        principalsMatch(d.writePrincipals, viewer))
        .map((d) => d.key));
    return { readable, writable };
}
/** Okunabilir anahtarlarla sınırlı değer sözlüğü (silinmiş tanım ve gizli alan değerleri düşer, K-5). */
export function projectCustomValues(values, readable) {
    const out = {};
    if (!values)
        return out;
    for (const [k, v] of Object.entries(values))
        if (readable.has(k))
            out[k] = v;
    return out;
}
/**
 * Kullanıcıya dönen tanım görünümü (metadata ve form): principal listeleri ve `allowedRecordIds` YOK (K-6);
 * `writable` alan düzeyi yazma kararıdır (kayıt düzeyi yazma ayrıca gerekir).
 */
export const customFieldViewOptionsSchema = customFieldOptionsSchema.omit({
    allowedRecordIds: true,
});
export const customFieldViewSchema = customFieldDefSchema
    .omit({ readPrincipals: true, writePrincipals: true, options: true })
    .extend({ options: customFieldViewOptionsSchema, writable: z.boolean() });
/**
 * Açık İZİN LİSTESİYLE kurulur (hariç tutma listesiyle değil): tanıma ileride eklenecek hassas bir alan kendiliğinden
 * kullanıcıya sızmaz. `allowedRecordIds` ve principal listeleri bilerek yoktur.
 */
export function toCustomFieldView(def, writable) {
    const o = def.options;
    const options = {};
    if (o.maxLength !== undefined)
        options.maxLength = o.maxLength;
    if (o.min !== undefined)
        options.min = o.min;
    if (o.max !== undefined)
        options.max = o.max;
    if (o.decimals !== undefined)
        options.decimals = o.decimals;
    if (o.currency !== undefined)
        options.currency = o.currency;
    if (o.choices !== undefined)
        options.choices = o.choices;
    if (o.ratingMax !== undefined)
        options.ratingMax = o.ratingMax;
    if (o.recordType !== undefined)
        options.recordType = o.recordType;
    if (o.recordTypes !== undefined)
        options.recordTypes = o.recordTypes;
    if (o.addToWriters !== undefined)
        options.addToWriters = o.addToWriters;
    if (o.notify !== undefined)
        options.notify = o.notify;
    if (o.expression !== undefined)
        options.expression = o.expression;
    if (o.resultType !== undefined)
        options.resultType = o.resultType;
    if (o.placeholder !== undefined)
        options.placeholder = o.placeholder;
    if (o.help !== undefined)
        options.help = o.help;
    return {
        id: def.id,
        recordType: def.recordType,
        key: def.key,
        label: def.label,
        type: def.type,
        options,
        required: def.required,
        readonly: def.readonly,
        hideInTemplates: def.hideInTemplates,
        hideInWebForms: def.hideInWebForms,
        showWhenEmpty: def.showWhenEmpty,
        askOnQualify: def.askOnQualify,
        condition: def.condition,
        visibleFor: def.visibleFor,
        sortOrder: def.sortOrder,
        createdAt: def.createdAt,
        updatedAt: def.updatedAt,
        writable,
    };
}
/**
 * Projekte tanım listesi: yalnız okunabilir alanlar, sıra korunur. Anahtarlar kayıt tipi içinde tekildir; bağımlılık
 * kuralı her kayıt tipi için ayrı değerlendirilir (farklı tiplerde aynı anahtar birbirini etkilemez).
 */
export function customFieldViewsFor(defs, viewer) {
    const byType = new Map();
    for (const d of defs)
        byType.set(d.recordType, [...(byType.get(d.recordType) ?? []), d]);
    const access = new Map([...byType].map(([type, list]) => [type, fieldAccessFor(list, viewer)]));
    return defs
        .filter((d) => access.get(d.recordType)?.readable.has(d.key) === true)
        .map((d) => toCustomFieldView(d, access.get(d.recordType)?.writable.has(d.key) === true));
}
//# sourceMappingURL=field-access.js.map